import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
} from "reactstrap";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { deleteFerrailleur, deleteGroupe, loadFerrailleurs, loadGroupes, saveFerrailleur, saveGroupe } from "./reseauService";

const emptyGroupe = { nom: "", categorie: "A", description: "", actif: true };

const emptyFerrailleur = {
  nom: "",
  raisonSociale: "",
  ice: "",
  telephone: "",
  email: "",
  ville: "",
  actif: true,
  groupeIds: [],
  createLoginAccount: false,
  loginUsername: "",
  loginPassword: "",
};

const emptyLoginAccount = {
  ferrailleur: null,
  loginUsername: "",
  loginPassword: "",
};

const emptyShareLogin = {
  open: false,
  ferrailleur: null,
  whatsappPhone: "",
  loginUsername: "",
  loginPassword: "",
  message: "",
};

function ReseauPage() {
  const [groupes, setGroupes] = useState([]);
  const [ferrailleurs, setFerrailleurs] = useState([]);
  const [groupeForm, setGroupeForm] = useState(emptyGroupe);
  const [ferrailleurForm, setFerrailleurForm] = useState(emptyFerrailleur);
  const [loginForm, setLoginForm] = useState(emptyLoginAccount);
  const [shareLogin, setShareLogin] = useState(emptyShareLogin);
  const [loading, setLoading] = useState(false);
  const [savingGroupe, setSavingGroupe] = useState(false);
  const [savingFerrailleur, setSavingFerrailleur] = useState(false);
  const [savingLogin, setSavingLogin] = useState(false);
  const [groupeQuery, setGroupeQuery] = useState("");
  const [ferrailleurQuery, setFerrailleurQuery] = useState("");
  const [mobileFilter, setMobileFilter] = useState("");
  const [notice, setNotice] = useState(null);
  const [buyerValidationError, setBuyerValidationError] = useState("");
  const [buyerModalOpen, setBuyerModalOpen] = useState(false);
  const [groupeModalOpen, setGroupeModalOpen] = useState(false);
  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const [buyerDeleteTarget, setBuyerDeleteTarget] = useState(null);
  const [deletingBuyer, setDeletingBuyer] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [groupesResponse, ferrailleursResponse] = await Promise.all([loadGroupes(), loadFerrailleurs()]);
      setGroupes(groupesResponse?.data?.body || []);
      setFerrailleurs(ferrailleursResponse?.data?.body || []);
    } catch (error) {
      setNotice({ color: "danger", message: getErrorMessage(error, "Impossible de charger le reseau.") });
    } finally {
      setLoading(false);
    }
  };

  const openCreateBuyer = () => {
    setFerrailleurForm(emptyFerrailleur);
    setBuyerValidationError("");
    setBuyerModalOpen(true);
  };

  const openEditBuyer = (ferrailleur) => {
    setBuyerValidationError("");
    setFerrailleurForm({
      ...emptyFerrailleur,
      ...ferrailleur,
      groupeIds: ferrailleur.groupeIds || [],
      createLoginAccount: false,
      loginPassword: "",
    });
    setBuyerModalOpen(true);
  };

  const openCreateGroupe = () => {
    setGroupeForm(emptyGroupe);
    setGroupeModalOpen(true);
  };

  const openEditGroupe = (groupe) => {
    setGroupeForm(groupe);
    setGroupeModalOpen(true);
  };

  const openLoginAccount = (ferrailleur) => {
    setLoginForm({
      ferrailleur,
      loginUsername: ferrailleur.loginUsername || ferrailleur.telephone || ferrailleur.email || "",
      loginPassword: "",
    });
    setLoginModalOpen(true);
  };

  const closeBuyerModal = () => {
    setBuyerModalOpen(false);
    setFerrailleurForm(emptyFerrailleur);
    setBuyerValidationError("");
  };

  const closeGroupeModal = () => {
    setGroupeModalOpen(false);
    setGroupeForm(emptyGroupe);
  };

  const closeLoginModal = () => {
    setLoginModalOpen(false);
    setLoginForm(emptyLoginAccount);
  };

  const closeShareLoginModal = () => {
    setShareLogin(emptyShareLogin);
  };

  const saveGroupeForm = async (event) => {
    event.preventDefault();
    setSavingGroupe(true);
    try {
      await saveGroupe(groupeForm);
      closeGroupeModal();
      await loadData();
      setNotice({ color: "success", message: "Groupe enregistre avec succes." });
    } catch (error) {
      setNotice({ color: "danger", message: getErrorMessage(error, "Impossible d'enregistrer le groupe.") });
    } finally {
      setSavingGroupe(false);
    }
  };

  const saveFerrailleurForm = async (event) => {
    event.preventDefault();
    const duplicateError = findDuplicateBuyer(ferrailleurs, ferrailleurForm);
    if (duplicateError) {
      setBuyerValidationError(duplicateError);
      return;
    }
    setBuyerValidationError("");
    setSavingFerrailleur(true);
    try {
      await saveFerrailleur({
        ...ferrailleurForm,
        createLoginAccount: false,
        loginUsername: "",
        loginPassword: "",
      });
      closeBuyerModal();
      await loadData();
      setNotice({ color: "success", message: "Acheteur enregistre avec succes." });
    } catch (error) {
      const message = getErrorMessage(error, "Impossible d'enregistrer l'acheteur.");
      setBuyerValidationError(message);
      setNotice({ color: "danger", message });
    } finally {
      setSavingFerrailleur(false);
    }
  };

  const saveLoginForm = async (event) => {
    event.preventDefault();
    const ferrailleur = loginForm.ferrailleur;
    if (!ferrailleur) return;

    const username = loginForm.loginUsername || ferrailleur.telephone || ferrailleur.email || "";
    const password = loginForm.loginPassword || "";
    setSavingLogin(true);
    try {
      await saveFerrailleur({
        ...ferrailleur,
        groupeIds: ferrailleur.groupeIds || [],
        createLoginAccount: true,
        loginUsername: username,
        loginPassword: password,
      });
      closeLoginModal();
      await loadData();
      setNotice({
        color: "success",
        message: `Compte login enregistre. Identifiant: ${username}.`,
      });
      openShareLogin({
        ...ferrailleur,
        loginUsername: username,
      }, password);
    } catch (error) {
      setNotice({ color: "danger", message: getErrorMessage(error, "Impossible d'enregistrer le compte login.") });
    } finally {
      setSavingLogin(false);
    }
  };

  const removeGroupe = async (id) => {
    try {
      await deleteGroupe(id);
      await loadData();
      setNotice({ color: "success", message: "Groupe supprime avec succes." });
    } catch (error) {
      setNotice({ color: "danger", message: getErrorMessage(error, "Impossible de supprimer le groupe.") });
    }
  };

  const confirmDeleteFerrailleur = async () => {
    if (!buyerDeleteTarget) return;
    setDeletingBuyer(true);
    try {
      await deleteFerrailleur(buyerDeleteTarget.id);
      setBuyerDeleteTarget(null);
      await loadData();
      setNotice({ color: "success", message: "Acheteur supprime avec succes." });
    } catch (error) {
      setNotice({ color: "danger", message: getErrorMessage(error, "Impossible de supprimer l'acheteur.") });
    } finally {
      setDeletingBuyer(false);
    }
  };

  const toggleGroupForFerrailleur = (id) => {
    setFerrailleurForm((prev) => {
      const current = prev.groupeIds || [];
      return { ...prev, groupeIds: current.includes(id) ? current.filter((item) => item !== id) : [...current, id] };
    });
  };

  const openShareLogin = (ferrailleur, password = "") => {
    const loginUsername = ferrailleur.loginUsername || ferrailleur.telephone || ferrailleur.email || "";
    const whatsappPhone = ferrailleur.telephone || "";
    setShareLogin({
      open: true,
      ferrailleur,
      whatsappPhone,
      loginUsername,
      loginPassword: password,
      message: buildLoginMessage(ferrailleur, loginUsername, password),
    });
  };

  const updateShareField = (field, value) => {
    setShareLogin((prev) => {
      const next = { ...prev, [field]: value };
      if (["loginUsername", "loginPassword"].includes(field)) {
        next.message = buildLoginMessage(prev.ferrailleur, next.loginUsername, next.loginPassword);
      }
      return next;
    });
  };

  const shareLoginViaWhatsapp = () => {
    const phone = normalizeWhatsappPhone(shareLogin.whatsappPhone);
    if (!phone) {
      setNotice({ color: "warning", message: "Veuillez renseigner un numero WhatsApp valide." });
      return;
    }
    const message = shareLogin.message || buildLoginMessage(shareLogin.ferrailleur, shareLogin.loginUsername, shareLogin.loginPassword);
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(message)}`, "_blank", "noopener,noreferrer");
  };

  const counts = useMemo(() => ({
    groupes: groupes.length,
    ferrailleurs: ferrailleurs.length,
    actifs: ferrailleurs.filter((item) => item.actif).length,
    comptes: ferrailleurs.filter((item) => item.hasLoginAccount).length,
  }), [groupes, ferrailleurs]);

  const filteredGroupes = useMemo(() => {
    const query = groupeQuery.trim().toLowerCase();
    if (!query) return groupes;
    return groupes.filter((groupe) => [groupe.nom, groupe.description, groupe.categorie].filter(Boolean).join(" ").toLowerCase().includes(query));
  }, [groupes, groupeQuery]);

  const filteredFerrailleurs = useMemo(() => {
    const query = ferrailleurQuery.trim().toLowerCase();
    return ferrailleurs.filter((ferrailleur) => {
      const matchesQuery = !query || [
        ferrailleur.nom,
        ferrailleur.raisonSociale,
        ferrailleur.telephone,
        ferrailleur.ville,
        ferrailleur.email,
        (ferrailleur.groupeNoms || []).join(" "),
      ].filter(Boolean).join(" ").toLowerCase().includes(query);
      const matchesMobile = !mobileFilter || (mobileFilter === "with" ? ferrailleur.hasLoginAccount : !ferrailleur.hasLoginAccount);
      return matchesQuery && matchesMobile;
    });
  }, [ferrailleurs, ferrailleurQuery, mobileFilter]);

  return (
    <div className="page-content auction-page reseau-page">
      <Container fluid>
        <Breadcrumbs title="Réseau" breadcrumbItems={[{ title: "Modules", link: "#" }, { title: "Réseau", link: "#" }]} />

        {notice && (
          <Alert color={notice.color} toggle={() => setNotice(null)} className="reseau-alert">
            {notice.message}
          </Alert>
        )}

        <Row className="g-4">
          <Col xl={8}>
            <Card className="reseau-list-card">
              <CardBody>
                <div className="reseau-card-toolbar">
                  <div>
                    <h4 className="mb-1">Acheteurs</h4>
                    <p className="text-muted mb-0">{filteredFerrailleurs.length} profil(s) affiche(s)</p>
                  </div>
                  <div className="d-flex flex-wrap gap-2">
                    <Button color="primary" onClick={openCreateBuyer}>
                      <i className="ri-user-add-line me-1" />
                      Créer acheteur
                    </Button>
                    <div className="reseau-search-control">
                      <i className="ri-search-line" />
                      <Input value={ferrailleurQuery} onChange={(event) => setFerrailleurQuery(event.target.value)} placeholder="Nom, telephone, ville..." />
                    </div>
                    <Input className="reseau-filter-select" type="select" value={mobileFilter} onChange={(event) => setMobileFilter(event.target.value)}>
                      <option value="">Tous</option>
                      <option value="with">Avec compte</option>
                      <option value="without">Sans compte</option>
                    </Input>
                  </div>
                </div>
                <div className="stack-list">
                  {loading ? (
                    <div className="reseau-loading"><Spinner size="sm" /> Chargement...</div>
                  ) : filteredFerrailleurs.map((ferrailleur) => (
                    <div className="stack-row reseau-stack-row" key={ferrailleur.id}>
                      <div>
                        <strong>{ferrailleur.nom}</strong>
                        <span>{[ferrailleur.telephone, ferrailleur.ville, ferrailleur.email].filter(Boolean).join(" - ") || "Coordonnees non renseignees"}</span>
                        <span>{(ferrailleur.groupeNoms || []).join(", ") || "Aucun groupe"}</span>
                      </div>
                      <div className="d-flex gap-2 align-items-center">
                        <Badge color={ferrailleur.actif ? "success" : "secondary"} className={ferrailleur.actif ? "text-white" : ""}>{ferrailleur.actif ? "Actif" : "Inactif"}</Badge>
                        <Badge color={ferrailleur.hasLoginAccount ? "light" : "light"} className={ferrailleur.hasLoginAccount ? "text-white" : "text-dark"}>{ferrailleur.hasLoginAccount ? "Compte login" : "Sans compte"}</Badge>
                        <Button size="sm" color="light" onClick={() => openLoginAccount(ferrailleur)}>
                          {ferrailleur.hasLoginAccount ? "Se connecter" : "Créer login"}
                        </Button>
                        <Button size="sm" color="light" className="text-success" onClick={() => openShareLogin(ferrailleur)}>
                          <i className="ri-whatsapp-line me-1" />
                          WhatsApp
                        </Button>
                        <Button size="sm" color="light" onClick={() => openEditBuyer(ferrailleur)}>Modifier</Button>
                        <Button size="sm" color="light" className="text-danger" onClick={() => setBuyerDeleteTarget(ferrailleur)}>Supprimer</Button>
                      </div>
                    </div>
                  ))}
                  {!loading && !filteredFerrailleurs.length && <div className="reseau-empty-state">Aucun acheteur trouve.</div>}
                </div>
              </CardBody>
            </Card>
          </Col>

          <Col xl={4}>
            <Card className="reseau-list-card">
              <CardBody>
                <div className="reseau-card-toolbar">
                  <div>
                    <h4 className="mb-1">Groupes</h4>
                    <p className="text-muted mb-0">{filteredGroupes.length} groupe(s)</p>
                  </div>
                  <div className="d-flex flex-wrap gap-2">
                    <Button color="primary" onClick={openCreateGroupe}>
                      <i className="ri-add-line me-1" />
                      Créer
                    </Button>
                    <div className="reseau-search-control">
                      <i className="ri-search-line" />
                      <Input value={groupeQuery} onChange={(event) => setGroupeQuery(event.target.value)} placeholder="Rechercher..." />
                    </div>
                  </div>
                </div>
                <div className="stack-list">
                  {loading ? (
                    <div className="reseau-loading"><Spinner size="sm" /> Chargement...</div>
                  ) : filteredGroupes.map((groupe) => (
                    <div className="stack-row reseau-stack-row" key={groupe.id}>
                      <div>
                        <strong>{groupe.nom}</strong>
                        <span>{groupe.description || "Aucune description"}</span>
                      </div>
                      <div className="d-flex gap-2 align-items-center">
                        <Badge color="info">{groupe.categorie}</Badge>
                        <Button size="sm" color="light" onClick={() => openEditGroupe(groupe)}>Modifier</Button>
                        <Button size="sm" color="light" className="text-danger" onClick={() => removeGroupe(groupe.id)}>Supprimer</Button>
                      </div>
                    </div>
                  ))}
                  {!loading && !filteredGroupes.length && <div className="reseau-empty-state">Aucun groupe trouve.</div>}
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>

        <Modal isOpen={buyerModalOpen} toggle={closeBuyerModal} size="xl" centered>
          <form onSubmit={saveFerrailleurForm}>
            <ModalHeader toggle={closeBuyerModal}>{ferrailleurForm.id ? "Modifier acheteur" : "Créer acheteur"}</ModalHeader>
            <ModalBody>
              {buyerValidationError && <Alert color="danger">{buyerValidationError}</Alert>}
              <Row className="g-3 reseau-three-col-form">
                <Col lg={4} md={6}><Label>Nom</Label><Input required value={ferrailleurForm.nom} onChange={(e) => setFerrailleurForm({ ...ferrailleurForm, nom: e.target.value })} /></Col>
                <Col lg={4} md={6}><Label>Telephone</Label><Input value={ferrailleurForm.telephone || ""} onChange={(e) => setFerrailleurForm({ ...ferrailleurForm, telephone: e.target.value })} /></Col>
                <Col lg={4} md={6}><Label>Email</Label><Input type="email" value={ferrailleurForm.email || ""} onChange={(e) => setFerrailleurForm({ ...ferrailleurForm, email: e.target.value })} /></Col>
                <Col lg={4} md={6}><Label>Raison sociale</Label><Input value={ferrailleurForm.raisonSociale || ""} onChange={(e) => setFerrailleurForm({ ...ferrailleurForm, raisonSociale: e.target.value })} /></Col>
                <Col lg={4} md={6}><Label>ICE</Label><Input value={ferrailleurForm.ice || ""} onChange={(e) => setFerrailleurForm({ ...ferrailleurForm, ice: e.target.value })} /></Col>
                <Col lg={4} md={6}><Label>Ville</Label><Input value={ferrailleurForm.ville || ""} onChange={(e) => setFerrailleurForm({ ...ferrailleurForm, ville: e.target.value })} /></Col>
                <Col lg={4} md={6}>
                  <Label>Statut</Label>
                  <Input type="select" value={ferrailleurForm.actif ? "true" : "false"} onChange={(e) => setFerrailleurForm({ ...ferrailleurForm, actif: e.target.value === "true" })}>
                    <option value="true">Actif</option>
                    <option value="false">Inactif</option>
                  </Input>
                </Col>
                <Col xs={12}>
                  <Label>Groupes</Label>
                  <div className="choice-grid">
                    {groupes.map((groupe) => (
                      <button type="button" key={groupe.id} className={(ferrailleurForm.groupeIds || []).includes(groupe.id) ? "choice-pill active" : "choice-pill"} onClick={() => toggleGroupForFerrailleur(groupe.id)}>
                        {groupe.nom} <span>{groupe.categorie}</span>
                      </button>
                    ))}
                  </div>
                  {!groupes.length && <div className="text-muted small mt-2">Creez d'abord un groupe pour classer cet acheteur.</div>}
                </Col>
              </Row>
            </ModalBody>
            <ModalFooter>
              <Button color="light" type="button" onClick={closeBuyerModal}>Annuler</Button>
              <Button color="primary" type="submit" disabled={savingFerrailleur}>
                {savingFerrailleur && <Spinner size="sm" className="me-2" />}
                Enregistrer
              </Button>
            </ModalFooter>
          </form>
        </Modal>

        <Modal isOpen={loginModalOpen} toggle={closeLoginModal} centered>
          <form onSubmit={saveLoginForm}>
            <ModalHeader toggle={closeLoginModal}>Compte login</ModalHeader>
            <ModalBody>
              <div className="mb-3">
                <Label>Acheteur</Label>
                <Input value={loginForm.ferrailleur?.nom || ""} disabled />
              </div>
              <div className="mb-3">
                <Label>Identifiant</Label>
                <Input required value={loginForm.loginUsername} onChange={(e) => setLoginForm({ ...loginForm, loginUsername: e.target.value })} placeholder={loginForm.ferrailleur?.telephone || "0600000000"} />
              </div>
              <div>
                <Label>{loginForm.ferrailleur?.hasLoginAccount ? "Nouveau mot de passe" : "Mot de passe"}</Label>
                <Input type="password" required={!loginForm.ferrailleur?.hasLoginAccount} value={loginForm.loginPassword} onChange={(e) => setLoginForm({ ...loginForm, loginPassword: e.target.value })} />
                {loginForm.ferrailleur?.hasLoginAccount && <div className="text-muted small mt-1">Laissez vide pour garder le mot de passe actuel.</div>}
              </div>
            </ModalBody>
            <ModalFooter>
              <Button color="light" type="button" onClick={closeLoginModal}>Annuler</Button>
              <Button color="primary" type="submit" disabled={savingLogin}>
                {savingLogin && <Spinner size="sm" className="me-2" />}
                Enregistrer login
              </Button>
            </ModalFooter>
          </form>
        </Modal>

        <Modal isOpen={shareLogin.open} toggle={closeShareLoginModal} centered>
          <ModalHeader toggle={closeShareLoginModal}>Partager les acces WhatsApp</ModalHeader>
          <ModalBody>
            <div className="reseau-share-box mb-3">
              <strong>{shareLogin.ferrailleur?.nom || "Acheteur"}</strong>
              <span>{shareLogin.ferrailleur?.telephone || "Telephone non renseigne"}</span>
            </div>
            <Row className="g-3">
              <Col xs={12}>
                <Label>Numero WhatsApp</Label>
                <Input
                  value={shareLogin.whatsappPhone}
                  onChange={(event) => updateShareField("whatsappPhone", event.target.value)}
                  placeholder="Ex: 212600000000"
                />
                <div className="text-muted small mt-1">Par defaut le telephone du profil est utilise. Vous pouvez le changer si WhatsApp est different.</div>
              </Col>
              <Col md={6}>
                <Label>Identifiant</Label>
                <Input
                  value={shareLogin.loginUsername}
                  onChange={(event) => updateShareField("loginUsername", event.target.value)}
                  placeholder="Identifiant mobile"
                />
              </Col>
              <Col md={6}>
                <Label>Mot de passe a partager</Label>
                <Input
                  value={shareLogin.loginPassword}
                  onChange={(event) => updateShareField("loginPassword", event.target.value)}
                  placeholder="Saisir le mot de passe"
                />
                {!shareLogin.loginPassword && <div className="text-muted small mt-1">Le mot de passe actuel n'est pas affiche par securite.</div>}
              </Col>
              <Col xs={12}>
                <Label>Message</Label>
                <Input
                  type="textarea"
                  rows={7}
                  value={shareLogin.message}
                  onChange={(event) => updateShareField("message", event.target.value)}
                />
              </Col>
            </Row>
          </ModalBody>
          <ModalFooter>
            <Button color="light" type="button" onClick={closeShareLoginModal}>Annuler</Button>
            <Button color="success" type="button" onClick={shareLoginViaWhatsapp}>
              <i className="ri-whatsapp-line me-1" />
              Envoyer WhatsApp
            </Button>
          </ModalFooter>
        </Modal>

        <Modal isOpen={groupeModalOpen} toggle={closeGroupeModal} centered>
          <form onSubmit={saveGroupeForm}>
            <ModalHeader toggle={closeGroupeModal}>{groupeForm.id ? "Modifier groupe" : "Créer groupe"}</ModalHeader>
            <ModalBody>
              <Row className="g-3">
                <Col xs={12}><Label>Nom</Label><Input required value={groupeForm.nom} onChange={(e) => setGroupeForm({ ...groupeForm, nom: e.target.value })} /></Col>
                <Col xs={12}>
                  <Label>Categorie</Label>
                  <Input type="select" value={groupeForm.categorie} onChange={(e) => setGroupeForm({ ...groupeForm, categorie: e.target.value })}>
                    {["A", "B", "C", "TOUS"].map((item) => <option key={item}>{item}</option>)}
                  </Input>
                </Col>
                <Col xs={12}><Label>Description</Label><Input value={groupeForm.description || ""} onChange={(e) => setGroupeForm({ ...groupeForm, description: e.target.value })} /></Col>
              </Row>
            </ModalBody>
            <ModalFooter>
              <Button color="light" type="button" onClick={closeGroupeModal}>Annuler</Button>
              <Button color="primary" type="submit" disabled={savingGroupe}>
                {savingGroupe && <Spinner size="sm" className="me-2" />}
                Enregistrer
              </Button>
            </ModalFooter>
          </form>
        </Modal>

        <Modal isOpen={Boolean(buyerDeleteTarget)} toggle={() => !deletingBuyer && setBuyerDeleteTarget(null)} backdrop="static" centered>
          <ModalHeader toggle={() => !deletingBuyer && setBuyerDeleteTarget(null)}>Confirmer la suppression</ModalHeader>
          <ModalBody>
            Voulez-vous vraiment supprimer l'acheteur « {buyerDeleteTarget?.nom} » ? Cette action est irréversible.
          </ModalBody>
          <ModalFooter>
            <Button color="light" disabled={deletingBuyer} onClick={() => setBuyerDeleteTarget(null)}>Annuler</Button>
            <Button color="danger" disabled={deletingBuyer} onClick={confirmDeleteFerrailleur}>
              {deletingBuyer && <Spinner size="sm" className="me-2" />}Supprimer
            </Button>
          </ModalFooter>
        </Modal>
      </Container>
    </div>
  );
}

function getErrorMessage(error, fallback) {
  return error?.response?.data?.message || error?.response?.data?.body?.message || error?.message || fallback;
}

function findDuplicateBuyer(buyers, form) {
  const currentId = form.id;
  const name = normalizeDuplicateText(form.nom);
  const email = normalizeDuplicateText(form.email);
  const phone = String(form.telephone || "").replace(/\D/g, "");
  const others = buyers.filter((buyer) => buyer.id !== currentId);

  if (name && others.some((buyer) => normalizeDuplicateText(buyer.nom) === name)) {
    return "Un acheteur avec le même nom existe déjà.";
  }
  if (email && others.some((buyer) => normalizeDuplicateText(buyer.email) === email)) {
    return "Un acheteur avec le même email existe déjà.";
  }
  if (phone && others.some((buyer) => String(buyer.telephone || "").replace(/\D/g, "") === phone)) {
    return "Un acheteur avec le même téléphone existe déjà.";
  }
  return "";
}

function normalizeDuplicateText(value) {
  return String(value || "").trim().toLocaleLowerCase().replace(/\s+/g, " ");
}

function normalizeWhatsappPhone(value) {
  let phone = String(value || "").replace(/[^\d+]/g, "");
  if (!phone) return "";
  if (phone.startsWith("+")) {
    return phone.slice(1);
  }
  if (phone.startsWith("00")) {
    return phone.slice(2);
  }
  if (phone.startsWith("0")) {
    return `212${phone.slice(1)}`;
  }
  return phone;
}

function buildLoginMessage(ferrailleur, username, password) {
  return [
    `Bonjour ${ferrailleur?.nom || ""},`,
    "",
    "Vos acces a l'application mobile Jaeval VHU sont:",
    `Identifiant: ${username || "-"}`,
    `Mot de passe: ${password || "a renseigner"}`,
    "",
    "Lien de telechargement APK: https://jaeval.com/vhu-mobile",
    "",
    "Merci de garder ces informations confidentielles.",
  ].join("\n");
}

export default ReseauPage;
